/**
* Results Scroll JS
* -----------------------------------------------------------------------------
*
* All the JS for the Results Scroll component.
*/

// import {gsap} from 'gsap';
// import {ScrollTrigger} from 'gsap/ScrollTrigger.js';
// gsap.registerPlugin( ScrollTrigger );
import Splide from '@splidejs/splide';

( function( app ) {
  const COMPONENT = {
    className: 'results-scroll',
    selector() {
      return '.' + this.className;
    },

    init() {
      const sections = document.querySelectorAll( this.selector() );
      if ( !sections.length ) return;

      sections.forEach( ( section ) => {
        const listEl = section.querySelector( '.results-list' );
        const contentEl = section.querySelector( '.results-content' );
        const mobileContentEl = section.querySelector( '.mobile-results-content' );
        if ( !listEl || !contentEl || !mobileContentEl ) return;

        // =========================
        // TITLES (left, vertical loop)
        // =========================
        const titles = new Splide( listEl, {
          type: 'loop',
          direction: 'ttb',
          height: '580px',
          perPage: 5,
          focus: 'center',
          perMove: 1,
          gap: '24px',
          arrows: false,
          pagination: false,
          drag: true,
          autoplay: true,
          interval: 5000,
          speed: 1500,
          pauseOnHover: true,
          pauseOnFocus: false,
          resetProgress: false,
          waitForTransition: true,
        } );

        // Fixed indicator positioning (bottom of center slot)
        const track = listEl.querySelector( '.splide__track' );
        const indicator = track ? track.querySelector( '.active-indicator' ) : null;

        const positionIndicator = () => {
          if ( !track || !indicator ) return;
          const perPage = titles.options.perPage || 1;
          const trackH = track.clientHeight;
          const first = listEl.querySelector( '.splide__slide' );
          const gap = parseFloat( first ? getComputedStyle( first ).marginBottom : '0' ) || 0;
          const effective = trackH - gap * ( perPage - 1 );
          const slotH = effective / perPage;
          const centerIdx = Math.floor( perPage / 2 );
          const topPx = Math.round( slotH * ( centerIdx + 1 ) + gap * centerIdx );
          indicator.style.top = `${topPx}px`;
        };

        // Early-active for titles
        let tSlides; let tController;
        const setTitlesEarlyActive = ( index ) => {
          if ( !tSlides || !tController ) return;
          const real = tController.toIndex( index );
          tSlides.get().forEach( ( s ) => s.slide.classList.remove( 'is-early-active' ) );
          const target = tSlides.getAt( real );
          if ( target && target.slide ) target.slide.classList.add( 'is-early-active' );
        };

        titles.on( 'mounted', () => {
          ( {Slides: tSlides, Controller: tController} = titles.Components );
          setTitlesEarlyActive( titles.index );
          requestAnimationFrame( () => setTitlesEarlyActive( titles.index ) );
          positionIndicator();
        } );
        titles.on( 'move', ( newIndex ) => setTitlesEarlyActive( newIndex ) );
        titles.on( 'updated', () => setTitlesEarlyActive( titles.index ) );
        titles.on( 'resized', () => {
          setTitlesEarlyActive( titles.index );
          positionIndicator();
        } );

        // =========================
        // CONTENT (right) — FADE + our slide-up motion
        // =========================
        const content = new Splide( contentEl, {
          type: 'fade', // Splide crossfades opacity
          rewind: true, // avoids clone teleports
          height: '580px',
          perPage: 1,
          perMove: 1,
          arrows: false,
          pagination: false,
          drag: false,
          wheel: false,
          autoplay: false,
          speed: 1500, // keep in sync with your Tailwind/CSS duration
          waitForTransition: true,
        } );

        new Splide( mobileContentEl, {
          type: 'fade',
          rewind: true,
          height: '305px',
          perPage: 1,
          perMove: 1,
          arrows: true,
          pagination: false,
        } ).mount();

        let cSlides; let cController;

        // Helper: clear class after transition ends on the INNER panel
        const clearAfterTransition = ( slideEl, cls, ms ) => {
          // Find the inner panel (required for transitionend)
          const panel = slideEl.querySelector( '.content-panel' );
          const fallback = setTimeout( () => {
            slideEl.classList.remove( cls );
          }, ms + 50 );

          if ( !panel ) return; // fallback will handle it

          const onEnd = ( e ) => {
            if ( e.target !== panel ) return; // only when the panel finishes
            slideEl.classList.remove( cls );
            panel.removeEventListener( 'transitionend', onEnd );
            clearTimeout( fallback );
          };

          // ensure we don’t stack multiple listeners
          panel.removeEventListener( 'transitionend', onEnd );
          panel.addEventListener( 'transitionend', onEnd, {once: true} );
        };

        const setContentActive = ( index ) => {
          if ( !cSlides || !cController ) return;
          const real = cController.toIndex( index );
          cSlides.get().forEach( ( obj ) => obj.slide.classList.remove( 'is-content-active' ) );
          const target = cSlides.getAt( real )?.slide;
          if ( target ) target.classList.add( 'is-content-active' );
        };

        content.on( 'mounted', () => {
          ( {Slides: cSlides, Controller: cController} = content.Components );
          setContentActive( content.index );

          // Ensure initial slide animates from bottom to settled at 0
          const initial = cSlides.getAt( cController.toIndex( content.index ) )?.slide;
          if ( initial ) {
            initial.classList.add( 'is-entering' );
            clearAfterTransition( initial, 'is-entering', content.options.speed );
          }
        } );

        // When a slide becomes active: enter from bottom
        content.on( 'active', ( slide ) => {
          const el = slide.slide;
          setContentActive( slide.index );

          // in case of rapid nav, ensure it’s not marked leaving
          el.classList.remove( 'is-leaving' );

          el.classList.add( 'is-entering' );
          clearAfterTransition( el, 'is-entering', content.options.speed );
        } );

        // When a slide becomes inactive: go up & out
        content.on( 'inactive', ( slide ) => {
          const el = slide.slide;
          el.classList.remove( 'is-entering' );
          el.classList.add( 'is-leaving' );
          clearAfterTransition( el, 'is-leaving', content.options.speed );
        } );

        // =========================
        // Sync + mount
        // =========================
        titles.sync( content );
        titles.mount();
        content.mount();

        // Defensive cleanup if instance is ever destroyed
        const cleanup = () => {
          try {
            titles.destroy();
          } catch ( e ) {
            console.warn( 'Titles cleanup failed:', e );
          }
          try {
            content.destroy();
          } catch ( e ) {
            console.warn( 'Content cleanup failed:', e );
          }
        };
        section.addEventListener( 'll:results-scroll:destroy', cleanup, {once: true} );
      } );
    },

    finalize() {},
  };

  app.registerComponent( 'results-scroll', COMPONENT );
} )( app );
